import {
    Container,
    Text,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import {SizeEnum} from '@volkswagen-onehub/tokens'
import React from 'react'
import {addPercent, currencyFormat, numberFormat} from '../../shared/utilities'
import {Global} from '../../shared/variables'
import {SingleSimulation} from '../../types/SingleSimulation'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import {RootState} from '../rootReducer'
import {VisuallyHidden} from '../../shared/styled'

interface SimulationDataProps {
    promoData: SingleSimulation
    modelName: string
    color: TokenTextColor
}

export function SimulationData(props: SimulationDataProps): React.JSX.Element {
    const {t} = useTranslation('fa-flotte')

    const serviceType = useSelector(
        (state: RootState) => state.general.serviceType
    )

    const isLeasing = serviceType === Global.LEASING

    const advanceValue = currencyFormat(props.promoData.advance)

    const finalInstallmentValue = currencyFormat(
        props.promoData.final_installment
    )

    const kmValue = numberFormat(props.promoData.tot_km)

    const paymentValue = currencyFormat(props.promoData.monthly_payment)

    const tanValue = addPercent(props.promoData.tan)

    const taegValue = addPercent(props.promoData.taeg)

    return (
        <>
            <Text
                tag={TextTag.h4}
                color={props.color}
                appearance={TokenTextAppearance.bigcopy100}
            >
                <VisuallyHidden>{`${props.modelName} `}</VisuallyHidden>
                {props.promoData.version}
            </Text>
            <Container gutter={SizeEnum.static100} wrap="always">
                <Text
                    tag={TextTag.p}
                    color={props.color}
                    appearance={TokenTextAppearance.copy100}
                    bold
                >
                    <Text
                        tag={TextTag.span}
                        appearance={TokenTextAppearance.bigcopy100}
                    >
                        <span
                            dangerouslySetInnerHTML={{__html: paymentValue}}
                        />{' '}
                    </Text>
                    {t('simulation.monthlyPayment')}
                </Text>

                {isLeasing ? (
                    <Container gutter={SizeEnum.static100} wrap="always">
                        <Text
                            tag={TextTag.span}
                            color={props.color}
                            appearance={TokenTextAppearance.copy100}
                        >
                            {props.promoData.tan !== null && (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: `${
                                            isLeasing
                                                ? t('leasing.tan')
                                                : t('simulation.tan')
                                        } ${tanValue}
                                    ${
                                        props.promoData.has_fixed_rate
                                            ? t('simulation.fixedRate')
                                            : t('simulation.variableRate')
                                    }`,
                                    }}
                                />
                            )}
                            {props.promoData.tan && props.promoData.taeg && (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: `${Global.STRING_SEPARATOR}`,
                                    }}
                                />
                            )}
                            {props.promoData.taeg !== null && (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: `${t(
                                            'simulation.taeg'
                                        )} ${taegValue}`,
                                    }}
                                />
                            )}
                        </Text>
                        <Text
                            tag={TextTag.span}
                            color={props.color}
                            appearance={TokenTextAppearance.copy100}
                        >
                            {props.promoData.num_of_months && (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: `${
                                            props.promoData.num_of_months
                                        }
                                        ${t('simulation.months')}`,
                                    }}
                                />
                            )}
                            {props.promoData.num_of_months &&
                                props.promoData.tot_km && (
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: `${Global.STRING_SEPARATOR}`,
                                        }}
                                    />
                                )}
                            {props.promoData.tot_km && (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: `${kmValue} ${t(
                                            'simulation.km'
                                        )}`,
                                    }}
                                />
                            )}
                        </Text>
                        <Text
                            tag={TextTag.span}
                            color={props.color}
                            appearance={TokenTextAppearance.copy100}
                        >
                            {props.promoData.advance && (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: `
                                        ${t(
                                            'simulation.advance'
                                        )} ${advanceValue}`,
                                    }}
                                />
                            )}
                            {props.promoData.advance &&
                                props.promoData.final_installment && (
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: `${Global.STRING_SEPARATOR}`,
                                        }}
                                    />
                                )}
                            {props.promoData.final_installment && (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: `${t(
                                            'simulation.final-installment'
                                        )} ${finalInstallmentValue}`,
                                    }}
                                />
                            )}
                        </Text>
                    </Container>
                ) : (
                    <Text
                        tag={TextTag.span}
                        color={props.color}
                        appearance={TokenTextAppearance.copy100}
                    >
                        {props.promoData.advance && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: `${t(
                                        'simulation.advance'
                                    )} ${advanceValue}`,
                                }}
                            />
                        )}
                        {props.promoData.tot_km && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: `${
                                        Global.STRING_SEPARATOR
                                    } ${kmValue} ${t('simulation.km')}`,
                                }}
                            />
                        )}
                        {props.promoData.num_of_months && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: `${Global.STRING_SEPARATOR} ${
                                        props.promoData.num_of_months
                                    } ${t('simulation.months')}`,
                                }}
                            />
                        )}
                    </Text>
                )}
            </Container>
        </>
    )
}
